(() => {
  const ROOT_SELECTOR = '[data-mobilegs-style-studio]';
  const CROSSFADE_DURATION_MS = 260;
  const POSTER_SIZES = '(max-width: 899px) 74vw, 68vw';
  const VIEWER_MODULE_TIMEOUT_MS = 20000;
  const scriptUrl = document.currentScript?.src || document.baseURI;

  if (window.MobilegsStyleStudio) {
    window.MobilegsStyleStudio.scan(document);
    return;
  }

  const controllers = new WeakMap();
  const viewerModuleLoads = new Map();

  const loadViewerModule = (source) => {
    const url = new URL(source, scriptUrl);
    let entry = viewerModuleLoads.get(url.href);
    if (!entry) {
      entry = { promise: null, failures: 0 };
      viewerModuleLoads.set(url.href, entry);
    }
    if (!entry.promise) {
      // Browsers remember a failed import by URL. Refetch only after a terminal
      // failure, on the next explicit activation; preserve Shopify's version.
      if (entry.failures) url.searchParams.set('mgss_retry', String(entry.failures));
      const pending = import(url.href);
      entry.promise = pending;
      pending.catch(() => {
        if (entry.promise !== pending) return;
        entry.promise = null;
        entry.failures += 1;
      });
    }
    // A caller deadline must not discard a still-pending import. A newer retry
    // may share it, but the expired caller cannot continue into activation.
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('The 3D viewer script took too long to load.')), VIEWER_MODULE_TIMEOUT_MS);
      entry.promise.then(
        module => { window.clearTimeout(timer); resolve(module); },
        error => { window.clearTimeout(timer); reject(error); },
      );
    });
  };

  const initialize = (root) => {
    if (controllers.has(root)) return controllers.get(root);

    const options = [...root.querySelectorAll('[data-style-option]')];
    const stage = root.querySelector('[data-style-stage]');
    const viewerRegion = root.querySelector('[data-style-viewer-region]');
    const posterStack = root.querySelector('[data-style-poster-stack]');
    const posterLayers = [...root.querySelectorAll('[data-style-poster]')];
    const panel = root.querySelector('[data-style-panel]');
    const panelImage = root.querySelector('[data-style-panel-image]');
    const upperPanelImage = root.querySelector('[data-style-upper-panel-image]');
    const panelPlaceholder = root.querySelector('[data-style-panel-placeholder]');
    const selectedName = root.querySelector('[data-style-selected-name]');
    const selectedPrice = root.querySelector('[data-style-selected-price]');
    const shopLink = root.querySelector('[data-style-shop]');
    const activateButton = root.querySelector('[data-style-activate]');
    const activateLabel = activateButton?.querySelector('[data-style-activate-label]');
    const badge = root.querySelector('[data-style-badge]');
    const scrim = root.querySelector('[data-style-scrim]');
    const status = root.querySelector('[data-style-status]');

    if (
      !options.length ||
      !stage ||
      !viewerRegion ||
      !posterStack ||
      posterLayers.length !== 2 ||
      !selectedName ||
      !selectedPrice ||
      !shopLink ||
      !status
    ) {
      return null;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktopPanelMedia = window.matchMedia('(min-width: 750px)');
    const shopLabel = shopLink.textContent.trim();
    const assetCache = new Map();
    const cleanups = [];
    let selectedOption = options.find((option) => option.getAttribute('aria-checked') === 'true') || options[0];
    let activePoster = posterLayers.find((image) => image.classList.contains('is-active')) || posterLayers[0];
    let posterTransition = null;
    let selectionRequest = 0;
    let viewerController = null;
    let activationPromise = null;
    let activationRequest = 0;
    let destroyed = false;

    const listen = (target, type, listener, optionsValue) => {
      if (!target) return;
      target.addEventListener(type, listener, optionsValue);
      cleanups.push(() => target.removeEventListener(type, listener, optionsValue));
    };

    const isAvailable = (option) => option?.dataset.styleAvailable !== 'false';

    const preloadAsset = ({ src, srcset = '', sizes = '' }) => {
      if (!src) return Promise.resolve();
      const key = `${src}\n${srcset}\n${sizes}`;
      if (assetCache.has(key)) return assetCache.get(key);

      const request = new Promise((resolve, reject) => {
        const image = new Image();
        let settled = false;

        const resolveLoaded = () => {
          if (settled) return;
          settled = true;
          if (typeof image.decode === 'function') {
            image.decode().catch(() => {}).then(resolve);
          } else {
            resolve();
          }
        };
        const rejectFailed = () => {
          if (settled) return;
          settled = true;
          reject(new Error(`Could not load image: ${src}`));
        };

        image.decoding = 'async';
        image.addEventListener('load', resolveLoaded, { once: true });
        image.addEventListener('error', rejectFailed, { once: true });
        if (sizes) image.sizes = sizes;
        if (srcset) image.srcset = srcset;
        image.src = src;

        if (image.complete) {
          queueMicrotask(() => {
            if (image.naturalWidth > 0) resolveLoaded();
            else rejectFailed();
          });
        }
      }).catch((error) => {
        assetCache.delete(key);
        throw error;
      });

      assetCache.set(key, request);
      return request;
    };

    const posterAssetFor = (option) => ({
      src: option.dataset.stageSrc,
      srcset: option.dataset.stageSrcset || '',
      sizes: option.dataset.stageSrcset ? POSTER_SIZES : '',
    });

    const prefetchOption = (option) => {
      preloadAsset(posterAssetFor(option)).catch(() => {});
      const panelSrc = option.dataset.panelSrc;
      const upperPanelSrc = option.dataset.upperPanelSrc;
      if (panelSrc) preloadAsset({ src: panelSrc }).catch(() => {});
      if (upperPanelSrc && desktopPanelMedia.matches) preloadAsset({ src: upperPanelSrc }).catch(() => {});
    };

    const setActivationState = (label, busy = false) => {
      if (!activateButton) return;
      if (activateLabel) activateLabel.textContent = label;
      else activateButton.textContent = label;
      activateButton.disabled = busy;
      activateButton.setAttribute('aria-busy', String(busy));
    };

    const cancelPosterTransition = () => {
      if (!posterTransition) return;
      const { animation, incoming } = posterTransition;
      posterTransition = null;
      animation.cancel();
      incoming.classList.remove('is-entering');
      incoming.style.removeProperty('opacity');
      incoming.style.removeProperty('z-index');
    };

    const setPosterSource = (image, asset) => {
      if (asset.srcset) {
        image.srcset = asset.srcset;
        image.sizes = asset.sizes;
      } else {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      }
      image.src = asset.src;
    };

    const crossfadePoster = async (option, request) => {
      if (destroyed || request !== selectionRequest) return;
      const incoming = posterLayers.find((image) => image !== activePoster);
      const outgoing = activePoster;
      const asset = posterAssetFor(option);
      setPosterSource(incoming, asset);
      posterStack.setAttribute('aria-label', `${option.dataset.styleName} Mobilegs Ultra`);

      if (reducedMotion.matches || typeof incoming.animate !== 'function') {
        incoming.classList.add('is-active');
        outgoing.classList.remove('is-active');
        activePoster = incoming;
        return;
      }

      incoming.classList.remove('is-active');
      incoming.classList.add('is-entering');
      incoming.style.opacity = '0';
      incoming.style.zIndex = '2';
      const animation = incoming.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: CROSSFADE_DURATION_MS, easing: 'ease-out', fill: 'forwards' },
      );
      const transition = { animation, incoming, outgoing };
      posterTransition = transition;

      try {
        await animation.finished;
      } catch (error) {
        return;
      }
      if (destroyed || request !== selectionRequest || posterTransition !== transition) return;

      incoming.classList.add('is-active');
      outgoing.classList.remove('is-active');
      incoming.classList.remove('is-entering');
      activePoster = incoming;
      posterTransition = null;
      animation.cancel();
      incoming.style.removeProperty('opacity');
      incoming.style.removeProperty('z-index');
    };

    const setPanel = (option, { longAvailable = true, upperAvailable = true } = {}) => {
      if (!panel || !panelImage) return;
      const isClassic = option.dataset.classic === 'true';
      const longSrc = !isClassic && longAvailable ? option.dataset.panelSrc : '';
      const upperSrc = !isClassic && desktopPanelMedia.matches && upperAvailable
        ? option.dataset.upperPanelSrc
        : '';
      const showLong = Boolean(longSrc);
      const showUpper = Boolean(upperSrc && upperPanelImage);

      panel.classList.toggle('is-placeholder', isClassic);
      panel.classList.toggle('is-classic', isClassic);
      panel.classList.toggle('is-robins-egg', option.dataset.styleName === "Robin's Egg Blue");
      panel.classList.toggle('is-paper-airplane', option.dataset.styleName === 'Paper Airplane');
      if (panelPlaceholder) panelPlaceholder.hidden = !isClassic;

      if (showLong) {
        panelImage.src = longSrc;
        panelImage.hidden = false;
      } else {
        panelImage.hidden = true;
        panelImage.removeAttribute('src');
      }

      if (upperPanelImage) {
        upperPanelImage.dataset.src = option.dataset.upperPanelSrc || '';
        if (showUpper) {
          upperPanelImage.src = upperSrc;
          upperPanelImage.hidden = false;
        } else {
          upperPanelImage.hidden = true;
          upperPanelImage.removeAttribute('src');
        }
      }

      panel.hidden = !showLong && !showUpper && !isClassic;
      panel.setAttribute(
        'aria-label',
        isClassic
          ? panelPlaceholder?.textContent.trim() || 'Choose a pattern'
          : `${option.dataset.styleName} full design panel`,
      );
    };

    const updateVisualAssets = async (option, request) => {
      const posterAsset = posterAssetFor(option);
      const panelSrc = option.dataset.panelSrc;
      const upperPanelSrc = desktopPanelMedia.matches ? option.dataset.upperPanelSrc : '';
      const [posterResult, panelResult, upperPanelResult] = await Promise.allSettled([
        preloadAsset(posterAsset),
        panelSrc ? preloadAsset({ src: panelSrc }) : Promise.resolve(),
        upperPanelSrc ? preloadAsset({ src: upperPanelSrc }) : Promise.resolve(),
      ]);

      if (destroyed || request !== selectionRequest) return;
      setPanel(option, {
        longAvailable: panelResult.status === 'fulfilled',
        upperAvailable: upperPanelResult.status === 'fulfilled',
      });
      if (posterResult.status === 'rejected') {
        if (!viewerController) status.textContent = 'The selected product image is unavailable. Choose another design or use the shop link.';
        return;
      }
      await crossfadePoster(option, request);
    };

    const updateCommerce = (option) => {
      const available = isAvailable(option);
      selectedName.textContent = option.dataset.styleName;
      selectedPrice.textContent = option.dataset.stylePrice;
      root.dataset.selectedVariantId = option.dataset.styleVariantId || '';
      shopLink.setAttribute('aria-label', `${shopLabel}: ${option.dataset.styleName}`);
      shopLink.classList.toggle('is-disabled', !available);

      if (available && option.dataset.styleHref) {
        shopLink.href = option.dataset.styleHref;
        shopLink.removeAttribute('aria-disabled');
        shopLink.removeAttribute('tabindex');
      } else {
        shopLink.removeAttribute('href');
        shopLink.setAttribute('aria-disabled', 'true');
        shopLink.tabIndex = -1;
      }
    };

    const restoreImageMode = (message) => {
      // A newer selection can fail while initial activation still awaits an
      // older texture. Retire that activation before exposing a usable retry.
      activationRequest += 1;
      activationPromise = null;
      viewerController?.destroy();
      viewerController = null;
      root.classList.remove('is-3d', 'is-loading-3d');
      posterStack.hidden = false;
      if (badge) badge.hidden = false;
      if (scrim) scrim.hidden = false;
      if (activateButton) {
        activateButton.hidden = false;
        setActivationState(root.dataset.activateLabel || 'View in 3D');
      }
      status.textContent = message;
    };

    const updateSelection = (option, moveFocus = false) => {
      if (!option || destroyed || !isAvailable(option)) return;
      if (selectedOption === option) {
        if (moveFocus) option.focus({ preventScroll: true });
        return;
      }

      selectedOption = option;
      const request = ++selectionRequest;
      cancelPosterTransition();
      options.forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-checked', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      updateCommerce(option);
      if (!viewerController) status.textContent = '';
      updateVisualAssets(option, request).catch(() => {});

      if (moveFocus) {
        option.focus({ preventScroll: true });
        option.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      if (viewerController) {
        const activeViewerController = viewerController;
        activeViewerController.selectDesign(option).then(applied => {
          // Only the latest successfully applied design may restart the hint.
          // Selecting a still must never activate 3D or rearm its cue.
          if (applied !== false && !destroyed && viewerController === activeViewerController &&
              selectedOption === option && request === selectionRequest && root.classList.contains('is-3d')) {
            cue?.rearm();
          }
        }).catch(() => {
          if (
            destroyed ||
            viewerController !== activeViewerController ||
            selectedOption !== option ||
            request !== selectionRequest
          ) return;
          restoreImageMode('3D is unavailable for this design. The product image is still available.');
        });
      }

      root.dispatchEvent(new CustomEvent('mobilegs:style-change', {
        bubbles: true,
        detail: {
          name: option.dataset.styleName,
          href: option.dataset.styleHref,
          variantId: option.dataset.styleVariantId,
          available: isAvailable(option),
        },
      }));
    };

    const activate = ({ focusViewer = false } = {}) => {
      if (!activateButton || viewerController || destroyed) return activationPromise;
      if (activationPromise) return activationPromise;
      const request = ++activationRequest;

      activationPromise = (async () => {
        setActivationState(root.dataset.loadingLabel || 'Loading 3D...', true);
        root.classList.add('is-loading-3d');
        status.textContent = 'Loading interactive model';

        try {
          const module = await loadViewerModule(root.dataset.viewerModule);
          if (destroyed || request !== activationRequest) return;
          const activatedController = await module.activateStyleViewer({
            root,
            stage,
            viewerRegion,
            posterStack,
            status,
          });
          if (destroyed || request !== activationRequest) {
            activatedController.destroy();
            return;
          }
          viewerController = activatedController;

          while (!destroyed && request === activationRequest && viewerController === activatedController) {
            const optionAtStart = selectedOption;
            const applied = await activatedController.selectDesign(optionAtStart);
            if (destroyed || request !== activationRequest || viewerController !== activatedController) return;
            if (applied !== false && optionAtStart === selectedOption) break;
          }
          if (destroyed || request !== activationRequest || viewerController !== activatedController) return;

          activatedController.reveal();
          root.classList.remove('is-loading-3d');
          root.classList.add('is-3d');
          activateButton.hidden = true;
          if (badge) badge.hidden = true;
          if (scrim) scrim.hidden = true;
          setActivationState(root.dataset.activateLabel || 'View in 3D');
          if (focusViewer) activatedController.focus();
        } catch (error) {
          console.warn('Mobilegs Style Studio 3D activation failed.', error);
          if (!destroyed && request === activationRequest) {
            restoreImageMode(error.code === 'WEBGL_UNAVAILABLE'
              ? '3D is unavailable on this device. The product image is still available.'
              : '3D could not be loaded. The product image is still available.');
            if (focusViewer) {
              if (cue) cue.focus();
              else activateButton.focus({ preventScroll: true });
            }
          }
        } finally {
          if (request === activationRequest) activationPromise = null;
        }
      })();

      return activationPromise;
    };

    options.forEach((option) => {
      listen(option, 'click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        updateSelection(option);
      });
      listen(option, 'mouseenter', () => prefetchOption(option), { once: true });
      listen(option, 'focus', () => prefetchOption(option), { once: true });
      listen(option, 'keydown', (event) => {
        const availableOptions = options.filter(isAvailable);
        const current = availableOptions.indexOf(option);
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = availableOptions[(current + 1) % availableOptions.length];
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = availableOptions[(current - 1 + availableOptions.length) % availableOptions.length];
        }
        if (event.key === 'Home') next = availableOptions[0];
        if (event.key === 'End') next = availableOptions[availableOptions.length - 1];
        if (next) {
          event.preventDefault();
          updateSelection(next, true);
          return;
        }
        if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          updateSelection(option);
        }
      });
    });

    listen(shopLink, 'click', (event) => {
      if (shopLink.getAttribute('aria-disabled') === 'true') event.preventDefault();
    });

    if (activateButton) {
      listen(activateButton, 'click', (event) => {
        activate({ focusViewer: event.detail === 0 });
      });
    }

    listen(desktopPanelMedia, 'change', () => {
      const option = selectedOption;
      const upperPanelSrc = option.dataset.upperPanelSrc;
      if (!desktopPanelMedia.matches || !upperPanelSrc) {
        setPanel(option);
        return;
      }
      preloadAsset({ src: upperPanelSrc })
        .then(() => {
          if (!destroyed && option === selectedOption) setPanel(option);
        })
        .catch(() => {
          if (!destroyed && option === selectedOption) {
            setPanel(option, { upperAvailable: false });
          }
        });
    });

    updateCommerce(selectedOption);
    setPanel(selectedOption);

    const controller = {
      activate,
      select(option) {
        updateSelection(option);
      },
      destroy() {
        destroyed = true;
        activationRequest += 1;
        selectionRequest += 1;
        cancelPosterTransition();
        cleanups.forEach((cleanup) => cleanup());
        viewerController?.destroy();
        viewerController = null;
        controllers.delete(root);
        delete root.dataset.styleStudioReady;
      },
    };

    controllers.set(root, controller);
    root.dataset.styleStudioReady = 'true';
    const cue = window.MobilegsViewerCue?.studio(root, focusViewer => activate({ focusViewer }));
    if (cue) cleanups.push(() => cue.destroy());
    return controller;
  };

  const scan = (scope) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  const destroyWithin = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => controllers.get(root)?.destroy());
  };

  window.MobilegsStyleStudio = { scan, destroyWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
